import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Save, AlertTriangle, Pencil, Check, X } from "lucide-react";

import {
  DeleteStakingRiskCategoryByIDThunk, GetStakingRiskAssessmentsThunk,
  GetStakingRiskClassificationsThunk,
  GetStakingRiskProductByIDThunk,
  UpdateStakingRiskProductThunk
} from "../../../Store/Thunks/StakingRiskThunk";
import { Tabs, TabsList, TabsTrigger } from "../../../Components/StakingRisk/ui/tabs";
import { Card, CardContent } from "../../../Components/StakingRisk/ui/card";
import { Button } from "../../../Components/StakingRisk/ui/button";
import infoIcon from "../../../Assets/img/icons/info.png"
import NewProductModal from "../dashboard/NewProductModal";
import { Popconfirm } from "antd";

export default function ProductHeader({
  productName,
  activeTab,
  setActiveTab,
  setActiveTabId,
  saveStatus,
}) {

  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const productId = useSelector((state) => state.StakingRiskReducer.createdStakingRiskId) || params?.id;
  const classifications = useSelector((state) => { return state.StakingRiskReducer.stakingRiskClassifications })
  const assessmentByIDdeleted = useSelector((state) => { return state.StakingRiskReducer.assessmentByIDdeleted })
  const productNamebyId = useSelector((state) => state.StakingRiskReducer.stakingRiskByID);
  const assessmentupdate = useSelector((state) => state.StakingRiskReducer.assessmentupdate);
  const productNameUpdated = useSelector((state) => state.StakingRiskReducer.updated);
  const classificationupdate = useSelector((state) => state.StakingRiskReducer.classificationupdate);

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(productNamebyId?.name || productName || "");
  const categoryCreated = useSelector((state) => { return state.StakingRiskReducer.categoryCreated })
  const categoryDeleted = useSelector((state) => { return state.StakingRiskReducer.categoryDeleted })
  const customParametercreated = useSelector((state) => { return state.StakingRiskReducer.customParametercreated })
  const [info, setInfo] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [recommendation, setRecommendation] = useState("");
  const [showExtra, setShowExtra] = useState(false);
  const [extraSaved, setExtraSaved] = useState(false);

  useEffect(() => {
    if (productNamebyId) {
      setInfo(
        productNamebyId.info
          ? productNamebyId.info.replace(/<br\s*\/?>/gi, "\n")
          : ""
      );
      setRecommendation(
        productNamebyId.recommendation
          ? productNamebyId.recommendation.replace(/<br\s*\/?>/gi, "\n")
          : ""
      );
    }
  }, [productNamebyId]);


  useEffect(() => {
    if (!isEditing && productNamebyId?.name) {
      setEditedName(productNamebyId?.name);
    }
  }, [productNamebyId?.name, isEditing, productName]);

  useEffect(() => {
    dispatch(GetStakingRiskProductByIDThunk(productId))
  }, [productNameUpdated, productId])

  useEffect(() => {

    if (productId !== 0 || assessmentByIDdeleted || assessmentupdate) {
      dispatch(GetStakingRiskClassificationsThunk(productId));
    }
    if (classificationupdate || assessmentupdate) {
      dispatch(GetStakingRiskProductByIDThunk(productId));
    }
  }, [productId, assessmentByIDdeleted, classificationupdate, assessmentupdate, categoryCreated, categoryDeleted, customParametercreated]);


  useEffect(() => {
    const selectedTab = classifications?.find(tab => tab.key === activeTab);
    if (selectedTab) {
      dispatch(GetStakingRiskAssessmentsThunk(selectedTab?.id));

    }
  }, [activeTab, classificationupdate, classifications, assessmentupdate]);


  const handleTitleSave = () => {
    if (!editedName.trim()) return;
    dispatch(UpdateStakingRiskProductThunk({ name: editedName }, productId));
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedName(productName || productNamebyId?.name || "");
    setIsEditing(false);
  };

  const handleSaveExtra = () => {
    if (!productId) return;

    dispatch(
      UpdateStakingRiskProductThunk(
        {
          info: info.trim()
            ? info.replace(/\r?\n/g, "<br />")
            : "",

          recommendation: recommendation.trim()
            ? recommendation.replace(/\r?\n/g, "<br />")
            : "",
        },
        productId,
        () => {
          setExtraSaved(true);
          setTimeout(() => setExtraSaved(false), 3000);
        }
      )
    );
  };

  const handleDeleteTab = (tab) => {
    dispatch(DeleteStakingRiskCategoryByIDThunk(tab.id));
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            {isEditing ? (
              <>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleSave();
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                  autoFocus
                  className="text-2xl md:text-3xl font-bold bg-transparent border-b border-gray-400 focus:outline-none focus:border-blue-600 dark:text-white"
                />
                <button onClick={handleTitleSave} title="Save" className="text-green-600 hover:text-green-800">
                  <Check className="h-5 w-5" />
                </button>
                <button onClick={handleCancelEdit} title="Cancel" className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <span
                  className="cursor-pointer hover:text-blue-600 flex items-center gap-1"
                  onClick={() => setIsEditing(true)}
                  title="Click to edit"
                >
                  {editedName} <Pencil className="h-5 w-5" />
                </span>
              </>
            )}
            {saveStatus === "saving" && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <span className="animate-pulse">Saving...</span>
              </span>
            )}
            {saveStatus === "saved" && (
              <span className="text-sm font-normal text-green-500 flex items-center gap-1">
                <Save className="h-4 w-4" /> Saved
              </span>
            )}
            {saveStatus === "error" && (
              <span className="text-sm font-normal text-red-500 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> Error saving
              </span>
            )}
            <img
              src={infoIcon}
              alt="Info and Recommendation"
              onClick={() => setShowExtra(!showExtra)}
              className="cursor-pointer"
              title="Show Info & Recommendation"
            />
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configure all aspects of your staking product
          </p>
        </div>
        <div className="flex gap-3">
          {!productName && (
            <>
              <Button
                onClick={() => { navigate('/stakingrisk/create-product') }}
                className="bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create Product
              </Button>
            </>
          )}
          <Link
            to={"/stakingrisk/dashboard"}>
            <Button variant="outline">
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
      {showExtra && (
        <div className="mt-4 space-y-3 border p-4 rounded-md bg-gray-50 dark:bg-gray-800">
          <div>
            <label className="block font-medium">Executive Summary</label>
            <textarea
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              className="w-full border rounded-md p-2"
              rows={3}
            />
          </div>
          <div>
            <label className="block font-medium">Recommendation</label>
            <textarea
              value={recommendation}
              onChange={(e) => setRecommendation(e.target.value)}
              className="w-full border rounded-md p-2"
              rows={3}
            />
          </div>
          <button
            onClick={handleSaveExtra}
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Save Info & Recommendation
          </button>
          {extraSaved && (
            <p className="text-green-600 font-medium mt-2">
              <span className="text-sm font-normal text-green-500 flex items-center gap-1">
                <Save className="h-4 w-4" /> Info & Recommendation saved successfully!
              </span>
            </p>
          )}
        </div>
      )}
      <Card className="overflow-x-auto border-none shadow-sm" style={{ maxWidth: "1200px" }}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Tabs
              value={activeTab}
              onValueChange={(newTabKey) => {
                setActiveTab(newTabKey);
                const selectedTab = classifications?.find(tab => tab?.key === newTabKey);
                if (selectedTab) {
                  dispatch(GetStakingRiskAssessmentsThunk(selectedTab.id));
                }
                setActiveTabId(selectedTab?.id)
              }}
              className="w-full overflow-x-auto"
            >
              <TabsList className="bg-gray-50 dark:bg-gray-800/50 p-0 h-auto flex flex-nowrap overflow-x-auto justify-start">
                {classifications.map(tab => (
                  <TabsTrigger
                    key={tab.key}
                    value={tab.key}
                    className="group relative py-3 px-6 pr-8 whitespace-nowrap
                        data-[state=active]:bg-white 
                        data-[state=active]:text-blue-600">  {tab.name}
                    <Popconfirm
                      title="Delete Category"
                      description="Do you really want to delete this category?"
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        handleDeleteTab(tab);
                      }}
                      onCancel={(e) => e?.stopPropagation()}
                      okText="Yes"
                      cancelText="No"
                    >
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="
                        absolute right-2 top-1/2 -translate-y-1/2
                        opacity-0 group-hover:opacity-100
                        transition-opacity duration-200
                        text-red-500 hover:text-red-700
                      "
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </Popconfirm>
                  </TabsTrigger>))}
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>
      <NewProductModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        mode="category"
        productId={productId}
      />
    </div>
  );
}